import { useEffect, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "../../../lib/motion/index.js";

/**
 * Centered modal used by the sheet's date/time pickers — the app's own pickers
 * in a centered overlay, NOT native `<input type=date/time>` (Android renders
 * those pickers' OK/Cancel off the bottom of the screen). Carries `.sheet-modal`
 * so the sheet's Escape handler defers to the open picker. Sits above the sheet
 * panel (z-48) but below the recurring/rollup confirms (z-50).
 */
export function SheetModal({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="sheet-modal fixed inset-0 z-[48] grid place-items-center bg-black/50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={reduce ? { duration: 0 } : { duration: 0.15 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <div
            className="max-h-[85vh] overflow-y-auto rounded-xl border bg-card p-3 shadow-2xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
